import React, { useState, useRef, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import { useFocusOnLoad } from '../../../../utils/animations'
import { allowEnterPress } from '../utils'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
  parseVASValue,
} from '../../utils/instrumentUtils'

const persistVASHorizontalSubmission = (props, value) => {
  try {
    const { question, id, webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = value
      ? [
        {
          label,
          value,
          question: id,
        },
      ]
      : []
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: value ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    completeQuestion(submission, webSurvey.order?.[0])
  } catch (e) {
    console.warn('VASHorizontal autosave failed', { error: e })
  }
}

const completeVASHorizontalQuestion = (props, value) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const answer = value
    ? [
      {
        label,
        value,
        question: id,
      },
    ]
    : []
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: value ? 'ANSWERED' : 'SKIPPED',
    answer
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

const VAS = ({
  topValue,
  topLabel,
  midValue,
  bottomValue,
  bottomLabel,
  onSelect,
  strings,
  hasInteracted,
  hint,
  handleClear,
  value
}) => {
  const sliderRef = useRef(null)
  const handleSliderClick = (event) => {
    if (sliderRef.current && !hasInteracted) {
      const { left, width } = sliderRef.current.getBoundingClientRect()
      const clickPosition = event.clientX - left
      let newValue = Math.round((clickPosition / width) * 100)
      newValue = newValue < 1 ? 1 : newValue
      onSelect(parseVASValue(newValue, 1))
    }
  }
  return (
    <div className='vas-container'>
      <div className='vas-header'>
        <span>{hint}</span>
        {/* {value ? <span>{value}%</span> : null} */}
        {value ? (
          <span className='vas-clear' onClick={handleClear}>
            Clear X
          </span>
        ) : null}
      </div>
      <div className='vas-top-values'>
        <span>{bottomValue}</span>
        <span>{midValue}</span>
        <span>{topValue}</span>
      </div>
      <div className='vas-slider' ref={sliderRef} onClick={handleSliderClick}>
        <input
          type='range'
          min='1'
          max='100'
          value={value}
          onChange={(evt) => {
            evt.preventDefault()
            onSelect(parseVASValue(evt.target.value, 1))
          }}
          className={`vas-input ${hasInteracted ? 'show-thumb' : ''}`}
        />
      </div>
      <div className='vas-labels'>
        <span>{bottomLabel}</span>
        <span>{topLabel}</span>
      </div>
    </div>
  )
}

const VASHorizontal = forwardRef((props, ref) => {
  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props

  const { attributes, label, scale_labels, hint } = question
  const { required } = attributes
  const { top_value, top, mid_value, bottom_value, bottom } = scale_labels
  const _className = className ? ` ${className}` : ''

  const [selectedValue, setSelectedValue] = useState('')
  const [hasInteracted, setHasInteracted] = useState(false)

  useEffect(() => {
    if (storedAnswer?.[0]?.value) {
      const storedValue = storedAnswer[0].value
      setAllRequiredAnswered(false, id)
      setSelectedValue(storedValue)
      setHasInteracted(true)
      if (!props.suppressAutoAdvance) {
        completeVASHorizontalQuestion(props, storedValue)
      } else {
        persistVASHorizontalSubmission(props, storedValue)
      }
    }
  }, [])

  const onSelect = (val) => {
    setAllRequiredAnswered(false, id)
    setSelectedValue(val)
    setHasInteracted(true)
    persistVASHorizontalSubmission(props, val)
  }

  const handleClear = () => {
    setSelectedValue('')
    setHasInteracted(false)
    persistVASHorizontalSubmission(props, '')
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div
          ref={ref}
          className={`survey-question vas-horizontal${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>
          <VAS
            topValue={top_value}
            bottomValue={bottom_value}
            midValue={mid_value}
            topLabel={top}
            bottomLabel={bottom}
            hint={hint}
            onSelect={onSelect}
            strings={strings}
            handleClear={handleClear}
            value={selectedValue}
            hasInteracted={hasInteracted}
          />
          {!isSibling && (
            <div className='character-count-row'>
              <QuestionFooter
                completed={!!selectedValue}
                id={id}
                isConsent={isConsent}
                isLastQuestion={isLastQuestion}
                loading={submissionLoading}
                onComplete={() =>
                  completeVASHorizontalQuestion(props, selectedValue)
                }
                question={question}
                renderBackButton={renderBackButton}
                strings={strings}
                submissionSurvey={submissionSurvey}
                webSurvey={webSurvey}
              />
            </div>
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
})

export default VASHorizontal

VASHorizontal.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string)
}
